import React, { useEffect, useState } from 'react';
import { CalendarX, Plus, Trash2 } from 'lucide-react';
import { showNotification } from '@mantine/notifications';
import vacacionesService from '../../../api/vacacionesService';
import { parseFechaLocal, formatearFechaISO } from '../../../utils/fechas';

// Días feriados que RRHH registra. Los de lunes a viernes no se cuentan en
// las solicitudes de vacaciones, permisos ni designaciones.
const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const campo = { padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem' };

const DiasFeriados = ({ alCambiar }) => {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState({ fecha: '', descripcion: '' });
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      setLista(await vacacionesService.getFeriados(`${anio}-01-01`, `${anio}-12-31`));
    } catch {
      showNotification({ title: 'Error', message: 'No se pudieron cargar los feriados', color: 'red' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [anio]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregar = async (e) => {
    e.preventDefault();
    if (!form.fecha || !form.descripcion.trim()) return;
    setGuardando(true);
    try {
      await vacacionesService.crearFeriado(form.fecha, form.descripcion.trim());
      showNotification({ title: 'Feriado registrado', message: `${formatearFechaISO(form.fecha)} - ${form.descripcion.trim()}`, color: 'green' });
      setForm({ fecha: '', descripcion: '' });
      if (parseFechaLocal(form.fecha).getFullYear() !== anio) setAnio(parseFechaLocal(form.fecha).getFullYear());
      else await cargar();
      alCambiar?.();
    } catch (error) {
      showNotification({ title: 'Error', message: error.response?.data?.error || 'No se pudo registrar el feriado', color: 'red' });
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (f) => {
    try {
      await vacacionesService.eliminarFeriado(f.id);
      showNotification({ title: 'Feriado eliminado', message: `${formatearFechaISO(f.fecha)} - ${f.descripcion}`, color: 'green' });
      await cargar();
      alCambiar?.();
    } catch (error) {
      showNotification({ title: 'Error', message: error.response?.data?.error || 'No se pudo eliminar el feriado', color: 'red' });
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <CalendarX size={20} /> Días feriados
      </h3>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: 0, marginBottom: '1.5rem' }}>
        Los feriados que caen de lunes a viernes no se cuentan en las solicitudes de vacaciones, permisos ni designaciones.
        Registre la fecha en que se disfruta el feriado (la trasladada, si aplica).
      </p>

      <form onSubmit={agregar} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#374151', marginBottom: '0.35rem' }}>Fecha</label>
          <input type="date" value={form.fecha} onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} style={campo} required />
        </div>
        <div style={{ flex: '1 1 16rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#374151', marginBottom: '0.35rem' }}>Descripción</label>
          <input type="text" value={form.descripcion} maxLength={150} placeholder="Ej.: Día de la Constitución (trasladado del 6)"
            onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} style={{ ...campo, width: '100%', boxSizing: 'border-box' }} required />
        </div>
        <button type="submit" disabled={guardando} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#2563eb', color: 'white', fontWeight: '600', cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? 0.7 : 1 }}>
          <Plus size={16} /> {guardando ? 'Guardando...' : 'Agregar'}
        </button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', color: '#374151' }}>Año</label>
        <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={campo}>
          {[anioActual - 1, anioActual, anioActual + 1, anioActual + 2].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{lista.length} feriado(s)</span>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left', color: '#374151' }}>
              <th style={{ padding: '0.6rem 0.9rem' }}>Fecha</th>
              <th style={{ padding: '0.6rem 0.9rem' }}>Día</th>
              <th style={{ padding: '0.6rem 0.9rem' }}>Descripción</th>
              <th style={{ padding: '0.6rem 0.9rem', width: '1%' }} />
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={4} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>Cargando...</td></tr>
            ) : lista.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No hay feriados registrados para {anio}.</td></tr>
            ) : lista.map(f => {
              const dia = parseFechaLocal(f.fecha).getDay();
              const finDeSemana = dia === 0 || dia === 6;
              return (
                <tr key={f.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.55rem 0.9rem', whiteSpace: 'nowrap' }}>{formatearFechaISO(f.fecha)}</td>
                  <td style={{ padding: '0.55rem 0.9rem', color: finDeSemana ? '#9ca3af' : '#111827' }}>
                    {DIAS[dia]}{finDeSemana ? ' (no afecta)' : ''}
                  </td>
                  <td style={{ padding: '0.55rem 0.9rem' }}>{f.descripcion}</td>
                  <td style={{ padding: '0.55rem 0.9rem' }}>
                    <button type="button" onClick={() => eliminar(f)} title="Quitar feriado"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.6rem', border: '1px solid #fecaca', borderRadius: '0.375rem', background: '#fef2f2', color: '#b91c1c', fontSize: '0.8rem', cursor: 'pointer' }}>
                      <Trash2 size={14} /> Quitar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiasFeriados;
